import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { map, Observable, of } from 'rxjs';
import { ICON } from '@shared/constants/icons.constant';
import { hasUnsupportedScope, SearchConnector, SearchResultItem, SearchScope } from '@shared/global-search';
import { Icon } from '@shared/types/icon.type';
import { EmailsService, EmailSearchHit } from '../../../shared/services/entity/emails.service';

const MAX_RESULTS = 6;

/**
 * Searches the synced emails by subject / sender / preview through the entity list filter.
 */
@Injectable({ providedIn: 'root' })
export class EmailsSearchConnector implements SearchConnector {
    // -----------------------------------------------------------------------------------------------------
    // @ DEPENDENCIES
    // -----------------------------------------------------------------------------------------------------

    private readonly entityService = inject(EmailsService);

    private readonly router = inject(Router);

    // -----------------------------------------------------------------------------------------------------
    // @ INSTANCE VARIABLES
    // -----------------------------------------------------------------------------------------------------

    readonly id = 'emails';

    readonly label = 'E-Mails';

    readonly order = 20;

    readonly icon: Icon = ICON.envelopeOpen;

    // -----------------------------------------------------------------------------------------------------
    // @ PUBLIC METHODS
    // -----------------------------------------------------------------------------------------------------

    search(query: string, scope: SearchScope): Observable<SearchResultItem[]> {
        // The email search honors every entity scope via the elastic term filters
        if (hasUnsupportedScope(scope, ['propertyId', 'unitId', 'contactId', 'contractorId'])) {
            return of([]);
        }

        const context = scope.context ?? {};

        return this.entityService
            .search(query, MAX_RESULTS, {
                propertyId: context['propertyId'] ? String(context['propertyId']) : undefined,
                unitId: context['unitId'] ? String(context['unitId']) : undefined,
                contactId: context['contactId'] ? String(context['contactId']) : undefined,
                contractorId: context['contractorId'] ? String(context['contractorId']) : undefined,
            })
            .pipe(map((hits) => hits.map((hit) => this.toResultItem(hit))));
    }

    // -----------------------------------------------------------------------------------------------------
    // @ PRIVATE METHODS
    // -----------------------------------------------------------------------------------------------------

    private toResultItem(email: EmailSearchHit): SearchResultItem {
        return {
            id: email.id,
            title: email.subject || '(kein Betreff)',
            subtitle: email.fromName || email.fromAddress,
            icon: this.icon,
            payload: email,
            actions: [
                {
                    id: 'open',
                    label: 'Öffnen',
                    icon: ICON.caretRight,
                    execute: () => {
                        this.router.navigate(['/emails', email.id]);
                    },
                },
            ],
        };
    }
}